import React, { useState, useEffect } from 'react';
import { apiFetch, useToast } from '../../App';
import { Plus, Send, MessageCircle, X } from 'lucide-react';

export default function PawsSupport() {
  const addToast = useToast();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [form, setForm] = useState({ subject: '', description: '', priority: 'MEDIUM' });
  const [reply, setReply] = useState('');

  useEffect(() => {
    apiFetch('/api/support/tickets').then(t => { setTickets(t); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    try {
      const ticket = await apiFetch('/api/support/tickets', { method: 'POST', body: JSON.stringify(form) });
      setTickets([ticket, ...tickets]);
      setShowCreate(false);
      setForm({ subject: '', description: '', priority: 'MEDIUM' });
      addToast('Ticket created!');
    } catch (e) { addToast(e.message, 'error'); }
  };

  const handleReply = async () => {
    if (!reply.trim()) return;
    try {
      await apiFetch(`/api/support/tickets/${selectedTicket.id}/respond`, { method: 'POST', body: JSON.stringify({ message: reply }) });
      const updated = await apiFetch(`/api/support/tickets/${selectedTicket.id}`);
      setSelectedTicket(updated);
      setReply('');
      addToast('Reply sent');
    } catch (e) { addToast(e.message, 'error'); }
  };

  return (
    <div data-testid="paws-support" className="max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Support</h1>
        <button onClick={() => setShowCreate(true)} className="btn-primary text-sm" data-testid="create-ticket-btn"><Plus size={16} /> New Ticket</button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2].map(i => <div key={i} className="skeleton h-20 rounded-2xl" />)}</div>
      ) : tickets.length === 0 ? (
        <div className="petfolk-card p-10 text-center">
          <MessageCircle size={40} className="mx-auto mb-3 text-petfolk-orange" />
          <p style={{ color: 'var(--text-secondary)' }}>No support tickets yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map(t => (
            <button key={t.id} onClick={() => { apiFetch(`/api/support/tickets/${t.id}`).then(setSelectedTicket); }} className="petfolk-card p-4 w-full text-left flex items-center gap-4" data-testid={`ticket-${t.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>{t.ticketNumber}</span>
                  <span className={`badge ${t.status === 'OPEN' ? 'badge-requested' : t.status === 'RESOLVED' ? 'badge-completed' : 'badge-in-progress'}`}>{t.status}</span>
                </div>
                <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{t.subject}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="petfolk-card w-full max-w-md mx-4 p-6 animate-slide-up" onClick={e => e.stopPropagation()} data-testid="create-ticket-modal">
            <div className="flex justify-between mb-4">
              <h2 className="font-outfit text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Create Ticket</h2>
              <button onClick={() => setShowCreate(false)}><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <input className="petfolk-input" placeholder="Subject" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} data-testid="ticket-subject" />
              <textarea className="petfolk-input" placeholder="Describe your issue..." rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="ticket-description" />
              <div className="flex gap-2">
                {['LOW', 'MEDIUM', 'HIGH'].map(p => (
                  <button key={p} onClick={() => setForm({...form, priority: p})} className={`flex-1 py-2 rounded-xl text-sm font-semibold ${form.priority === p ? 'bg-petfolk-orange text-white' : ''}`} style={{ border: '1px solid var(--border-color)', color: form.priority !== p ? 'var(--text-secondary)' : undefined }}>
                    {p}
                  </button>
                ))}
              </div>
              <button onClick={handleCreate} className="btn-primary w-full justify-center" data-testid="submit-ticket-btn">Submit Ticket</button>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="modal-overlay" onClick={() => setSelectedTicket(null)}>
          <div className="petfolk-card w-full max-w-lg mx-4 p-6 max-h-[80vh] overflow-auto animate-slide-up" onClick={e => e.stopPropagation()} data-testid="ticket-detail-modal">
            <div className="flex justify-between mb-4">
              <div>
                <span className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>{selectedTicket.ticketNumber}</span>
                <h2 className="font-outfit text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{selectedTicket.subject}</h2>
              </div>
              <button onClick={() => setSelectedTicket(null)}><X size={20} /></button>
            </div>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>{selectedTicket.description}</p>
            <div className="space-y-3 mb-4">
              {(selectedTicket.responses || []).map((r, i) => (
                <div key={i} className={`p-3 rounded-xl text-sm ${r.isStaff ? 'bg-petfolk-orange/5 ml-4' : 'bg-black/5 dark:bg-white/5 mr-4'}`}>
                  <p className="font-semibold text-xs mb-1" style={{ color: r.isStaff ? 'var(--petfolk-orange)' : 'var(--text-primary)' }}>
                    {r.isStaff ? 'Petfolk Support' : 'You'}
                  </p>
                  <p style={{ color: 'var(--text-primary)' }}>{r.message}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input className="petfolk-input flex-1" placeholder="Type a reply..." value={reply} onChange={e => setReply(e.target.value)} data-testid="ticket-reply-input" />
              <button onClick={handleReply} className="btn-primary px-4" data-testid="send-reply-btn"><Send size={16} /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
